import sharedLocalisationDict from '../../data/sharedLocalisation.json';
import defaultTranslationsDict from '../../data/defaultTranslations.json';
import { fetchResource } from '../../logic/asApi.ts';

// ============================================================================
// Localization constants
// ============================================================================

export const langAbbrev = {
  "en": "English",
  "de": "Deutsch",
  "es": "Español",
  "fr": "Français",
  "jp": "日本語",
  "cn": "中文",
  "it": "Italiano",
  "ko": "한국어",
  "pt": "Português",
  "ukr": "Українська",
  "dk": "Dansk",
  "nl": "Nederlands"
} as const;

const defaultTranslations: { [lang: string]: { [key: string]: string } } = defaultTranslationsDict as any;

// ============================================================================
// Localization controller
// ============================================================================

export class LocalizationController {
  private textSubscribers: Map<string, Set<HTMLElement>> = new Map();
  private subscriberVars: WeakMap<HTMLElement, any[]> = new WeakMap();
  private langText: { [lang: string]: { [key: string]: string } } | null = null;
  private readonly sharedLocalisation: { [lang: string]: { [key: string]: string } } = sharedLocalisationDict as any;
  public language: string;

  constructor(private readonly host: HTMLElement, initialLang: string = 'en') {
    this.language = initialLang;
  }

  fetchLangFiles(): void {
    const src = this.host.getAttribute('langFile') ?? '';
    if (!src) return;
    fetchResource(src)
      .then(data => {
        this.langText = data;
        this.updateLangSubscribers();
      })
      .catch(err => {
        console.error('Error loading language file:', err);
      });
  }

  updateLang(lang: string): void {
    this.language = lang;
    this.updateLangSubscribers();
    this._updateLangSelectors();
    this.host.dispatchEvent(new CustomEvent('as-language-changed', {
      bubbles: true,
      composed: true,
      detail: { lang }
    }));
    if (!this.langText) {
      this.fetchLangFiles();
    }
  }

  updateLangSubscribers(): void {
    this.textSubscribers.forEach((subscribers, textKey) => {
      subscribers.forEach(subscriber => {
        this.sendTextToSubscriber(textKey, subscriber);
      });
    });
  }

  getTextString(textKey: string): string | null {
    const lang = this.language;

    if (this.sharedLocalisation?.[lang]?.[textKey]) {
      return this.sharedLocalisation[lang][textKey];
    }
    if (this.langText?.[lang]?.[textKey]) {
      return this.langText[lang][textKey];
    }
    if (defaultTranslations[lang]?.[textKey]) {
      return defaultTranslations[lang][textKey];
    }
    if (textKey.startsWith('tooltip.')) {
      const simplifiedKey = textKey.substring(textKey.lastIndexOf('.') + 1);
      return simplifiedKey.charAt(0).toUpperCase() + simplifiedKey.slice(1);
    }

    return null;
  }

  sendTextToSubscriber(textKey: string, subscriber: HTMLElement): boolean {
    let text = this.getTextString(textKey);
    if (text === null) return false;

    const vars = this.subscriberVars.get(subscriber) || [];
    
    // Replace all {N} patterns
    text = text.replace(/\{(\d+)\}/g, (match, indexStr) => {
      const index = parseInt(indexStr, 10);
      const v = vars[index];
      
      if (v === undefined) {
        return "___"; // Fallback for missing variable
      }
      
      let valStr = '';
      if (typeof v === 'string') {
        valStr = v;
      } else if (v && typeof v === 'object' && v.dictKey) {
        valStr = this.getTextString(v.dictKey) ?? v.defaultText ?? '';
      }
      return valStr;
    });

    subscriber.dispatchEvent(new CustomEvent('as-local-text-update', {
      detail: { text }
    }));
    return true;
  }

  /**
   * Register all localization event listeners on the host element.
   * Call once from connectedCallback.
   */
  attachListeners(): void {
    this.host.addEventListener('as-local-text-subscribe', (event: CustomEvent) => {
      const { textKey, requester, vars } = event.detail;
      
      if (vars !== undefined) {
        this.subscriberVars.set(requester, vars);
      } else {
        this.subscriberVars.delete(requester);
      }

      if (!this.textSubscribers.has(textKey)) {
        this.textSubscribers.set(textKey, new Set());
      }
      this.textSubscribers.get(textKey)!.add(requester);
      this.sendTextToSubscriber(textKey, requester);
    });

    this.host.addEventListener('as-local-text-unsubscribe', (event: CustomEvent) => {
      const { textKey, requester } = event.detail;
      const subscribers = this.textSubscribers.get(textKey);
      if (subscribers) {
        subscribers.delete(requester);
        if (subscribers.size === 0) {
          this.textSubscribers.delete(textKey);
        }
      }
    });

    this.host.addEventListener('as-get-current-language', (event: CustomEvent) => {
      if (event.detail?.requester) {
        event.detail.requester.dispatchEvent(new CustomEvent('as-current-language', {
          detail: { language: this.language }
        }));
      }
    });

    this.host.addEventListener('as-get-init-lang', (event: CustomEvent) => {
      if (event.detail?.requester) {
        if (typeof event.detail.requester.setLanguage === 'function') {
          event.detail.requester.setLanguage(this.language);
        }
      }
    });

    this.host.addEventListener('as-change-language', (event: CustomEvent) => {
      this.updateLang(event.detail.lang);
    });
  }

  private _updateLangSelectors(): void {
    const langSelectors = this.host.querySelectorAll('as-lang-selector');
    langSelectors.forEach((selector: any) => {
      if (typeof selector.setLanguage === 'function') {
        selector.setLanguage(this.language);
      }
      selector.setAttribute('language', this.language);
    });
  }
}
